import * as RadixDialog from '@radix-ui/react-dialog';
import { useState } from 'react';
import type Stripe from 'stripe';
import stripeBanner from './stripe-banner.jpg';
import { LegacyButton } from '~/components/ui/LegacyButton';
import { StripeLogo } from '~/components/ui/logos/StripeLogo';
import { projectStore } from '~/lib/stores/project';
import { classNames } from '~/utils/classNames';
import { invariant } from '~/utils/invariant';
import { logger } from '~/utils/logger';

interface DialogContentSetAPIKeyProps {
  onClose: VoidFunction;
  setProducts: (products: Stripe.Product[]) => void;
  checkSecretThenGetProducts: (projectId: string, secretKey: string) => Promise<Array<Stripe.Product>>;
}

export function DialogContentSetApiKey({
  onClose,
  setProducts,
  checkSecretThenGetProducts,
}: DialogContentSetAPIKeyProps) {
  const [apiKey, setApiKey] = useState('');

  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isTestKey = apiKey.startsWith('sk_test_');
  const isProdKey = apiKey.startsWith('sk_live_');

  const handleApiKeyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setApiKey(value);
    setError(null);
  };

  const verifyApiKey = async () => {
    if (!apiKey.trim()) {
      setError('Please enter a valid API key');
      return;
    }

    if (!apiKey.startsWith('sk_test_') && !apiKey.startsWith('sk_live_')) {
      setError('Please enter a valid API key');
      return;
    }

    setIsVerifying(true);
    setError(null);

    try {
      const projectId = projectStore.projectId.get();

      invariant(projectId);

      const fetchedProducts = await checkSecretThenGetProducts(projectId, apiKey);
      setProducts(fetchedProducts);
    } catch (error) {
      logger.error(error);
      setError(error instanceof Error ? error.message : 'An unexpected error occured, please try again later.');
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <>
      <div
        className="w-[calc(100%+2px)] rounded-t-lg bg-cover h-25 -m-px"
        style={{
          backgroundImage: `url(${stripeBanner})`,
        }}
      />
      <StripeLogo size="xl" className="absolute top-25 left-1/2 -translate-x-1/2 -translate-y-1/2" />
      <div className="mt-16 mx-8 mb-6">
        <DialogHeader
          title="Enter your Stripe API key"
          description="Start generating revenue with your Bolt application."
        />

        <div className="w-full flex flex-col gap-4">
          <div className="flex items-center px-3 py-2.5 rounded-md border border-bolt-elements-borderColor focus-within:border-bolt-ds-brand-highlight bg-bolt-ds-surface text-bolt-elements-textSecondary [&>i]:focus-within:text-bolt-elements-textPrimary">
            <i className="shrink-0 i-ph:key size-5 mr-2" />
            <input
              type="password"
              value={apiKey}
              onChange={handleApiKeyChange}
              placeholder="Starts with sk_test or sk_live..."
              className="w-full bg-transparent text-bolt-elements-textPrimary placeholder:text-bolt-elements-textTertiary outline-none h-full"
            />
            {apiKey && (isTestKey || isProdKey) && (
              <div
                className={classNames('uppercase rounded px-1.5 py-1 text-xs -my-1 ml-1 -mr-0.5 shrink-0', {
                  'bg-bolt-ds-warning-container text-bolt-ds-on-warning-container': isTestKey,
                  'bg-bolt-ds-success-container text-bolt-ds-on-success-container': isProdKey,
                })}
              >
                {isTestKey ? 'Test' : 'Live'}
              </div>
            )}
          </div>

          {error && (
            <div className="flex items-center gap-2 text-red-500 bg-red-100 dark:bg-red-900/30 p-3 rounded-md">
              <span className="i-ph:warning-circle size-5" />
              <span>{error}</span>
              <button
                onClick={() => setError(null)}
                className="ml-auto text-bolt-elements-textSecondary hover:text-bolt-elements-textPrimary"
              >
                <span className="i-ph:x size-4" />
              </button>
            </div>
          )}

          <div className="bg-bolt-elements-background-depth-1 p-4 rounded-md">
            <div className="flex items-start gap-2">
              <span className="i-ph:question size-5 text-bolt-elements-textSecondary mt-0.5" />
              <div>
                <h3 className="text-bolt-elements-textPrimary font-medium mb-1">Where can I find my API key?</h3>
                <p className="text-bolt-elements-textSecondary text-sm">
                  Once your account is setup, please go to your{' '}
                  <a
                    href="https://dashboard.stripe.com/apikeys"
                    className="text-blue-500 hover:underline"
                    target="_blank"
                  >
                    developer settings
                  </a>{' '}
                  and copy the secret key.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-between">
          <LegacyButton variant="secondary" className="h-10" onClick={onClose}>
            Cancel
          </LegacyButton>
          <LegacyButton
            variant="primary"
            className="h-10"
            onClick={verifyApiKey}
            disabled={isVerifying || !apiKey.trim()}
          >
            {isVerifying && <span className="i-svg-spinners:90-ring-with-bg size-4 mr-2" />}
            Retrieve my products
          </LegacyButton>
        </div>
      </div>
    </>
  );
}

interface DialogHeaderProps {
  title: string;
  description: React.ReactNode;
}

function DialogHeader({ title, description }: DialogHeaderProps) {
  return (
    <>
      <RadixDialog.Title className="text-lg text-center font-semibold mb-1">{title}</RadixDialog.Title>
      <RadixDialog.Description className="flex flex-col gap-4 mb-5 text-center text-bolt-elements-textSecondary leading-6">
        {description}
      </RadixDialog.Description>
    </>
  );
}
